import React, { useEffect } from "react";
import { Link, useLocation } from "wouter";
import Footer from "@/components/Footer";
import { GlobalAdSlot } from "@/components/ads";
import { useAuth } from "@/contexts/AuthContext";
import { legalAndSupportRoutes, marketingHoldingPageRoutes } from "@/lib/marketingRoutes";
import AppEntryLink from "@/components/AppEntryLink";

interface PublicLayoutProps {
  children: React.ReactNode;
}

export default function PublicLayout({ children }: PublicLayoutProps) {
  const [location] = useLocation();
  const { isAuthenticated } = useAuth();
  const adFreeMarketingRoutes = [...marketingHoldingPageRoutes, ...legalAndSupportRoutes];
  const shouldShowAds = !adFreeMarketingRoutes.includes(location);

  // Scroll to top on route change.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="flex-none bg-white border-b border-gray-200 shadow-sm z-50">
        <div className="site-container flex items-center justify-between py-2.5 2xl:py-3.5">
          <Link href="/">
            <span className="flex items-center hover:scale-105 transition-transform duration-200">
              <img src="/logo.svg" alt="Wishlist Wizard" className="h-9 w-9 mr-2.5" />
              <span className="hidden font-bold text-xl tracking-tight text-emerald-800 sm:inline">Wishlist Wizard</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center space-x-6">
            <Link href="/how-it-works" className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">
              How It Works
            </Link>
            <Link href="/about" className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">
              About
            </Link>
            <Link href="/subscriptions" className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">
              Subscriptions
            </Link>
            <Link href="/download" className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">
              Download
            </Link>
            <Link href="/extension" className="text-gray-700 hover:text-emerald-700 font-medium transition-colors">
              Browser Extension
            </Link>
            {isAuthenticated ? (
              <Link href="/app/wishlists" className="px-6 py-2.5 bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg font-medium shadow-md hover:shadow-lg transition-all">
                Go to App
              </Link>
            ) : (
              <>
                <AppEntryLink href="/login" className="px-5 py-2 text-gray-700 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg font-medium transition-all">
                  Sign In
                </AppEntryLink>
                <AppEntryLink href="/register" className="px-6 py-2.5 bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg font-medium shadow-md hover:shadow-lg transition-all">
                  Sign Up
                </AppEntryLink>
              </>
            )}
          </nav>

          <div className="md:hidden flex items-center gap-3">
            <Link href="/how-it-works" className="text-gray-700 hover:text-emerald-800 font-medium">
              How It Works
            </Link>
            {isAuthenticated ? (
              <Link href="/app/wishlists" className="px-4 py-2 bg-emerald-800 text-white rounded-lg shadow-md">
                Go to App
              </Link>
            ) : (
              <>
                <AppEntryLink href="/login" className="text-gray-700 hover:text-emerald-800 font-medium">
                  Sign In
                </AppEntryLink>
                <AppEntryLink href="/register" className="px-4 py-2 bg-emerald-800 text-white rounded-lg shadow-md">
                  Sign Up
                </AppEntryLink>
              </>
            )}
          </div>
        </div>
      </header>

      {shouldShowAds && <GlobalAdSlot placement="top" />}

      {/* Content area between header and footer — flex-1 fills remaining space when short */}
      <main className="flex-1 bg-white">
        {children}
      </main>

      {shouldShowAds && <GlobalAdSlot placement="bottom" />}
      <Footer />
    </div>
  );
}
